import { useEffect, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export const DirectionChart = ({ directions }) => {
    const scrollContainerRef = useRef(null);
    // Custom dot component with direction-based colors
    const CustomDot = (props) => {
        const { cx, cy, payload } = props;
        const color = payload.direction === 'UP' ? '#22c55e' : '#ef4444'; // green for UP, red for DOWN
        
        return (
            <circle
                cx={cx}
                cy={cy}
                r={4}
                fill={color}
                stroke={color}
                strokeWidth={2}
            />
        );
    };

    // Transform direction data into chart-friendly format
    // UP increments value, DOWN decrements value
    const transformData = () => {
        if (!directions || directions.length === 0) return [];

        let cumulativeValue = 0;
        const chartData = directions
            .slice()
            .reverse() // Reverse to show oldest first
            .map((dir, index) => {
                // UP adds 1, DOWN subtracts 1
                if (dir.direction === 'UP') {
                    cumulativeValue += 1;
                } else if (dir.direction === 'DOWN') {
                    cumulativeValue -= 1;
                }

                return {
                    id: dir.id,
                    index: index + 1,
                    time: new Date(dir.time_placement).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                    }),
                    value: cumulativeValue,
                    direction: dir.direction,
                    timestamp: dir.time_placement
                };
            });

        return chartData;
    };

    const chartData = transformData();

    // Auto-scroll to latest (rightmost) data
    useEffect(() => {
        if (scrollContainerRef.current && chartData.length > 0) {
            scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth;
        }
    }, [chartData]);

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="bg-slate-800 border border-slate-700 p-3 rounded shadow-lg">
                    <p className="text-white text-sm font-medium mb-1">
                        Trade #{data.id}
                    </p>
                    <p className={`text-xs font-medium mb-1 ${
                        data.direction === 'UP' ? 'text-green-400' : 'text-red-400'
                    }`}>
                        Direction: {data.direction === 'UP' ? '↑' : '↓'} {data.direction}
                    </p>
                    <p className="text-xs text-slate-400 mb-1">
                        Time: {data.time}
                    </p>
                    <p className="text-xs text-blue-400">
                        Cumulative: {data.value}
                    </p>
                </div>
            );
        }
        return null;
    };

    if (chartData.length === 0) {
        return (
            <div className="flex items-center justify-center h-96 bg-slate-900 rounded-lg">
                <p className="text-slate-400 text-sm">No data to display</p>
            </div>
        );
    }

    return (
        <div className="bg-slate-900 p-4 rounded-lg">
            <div className="overflow-x-auto" ref={scrollContainerRef}>
                <div style={{ minWidth: chartData.length > 20 ? chartData.length * 40 : '100%' }}>
                    <ResponsiveContainer width="100%" height={400}>
                        <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis 
                        dataKey="time" 
                        stroke="#94a3b8"
                        tick={{ fill: '#94a3b8', fontSize: 11 }}
                        interval="preserveStartEnd"
                    />
                    <YAxis 
                        stroke="#94a3b8"
                        tick={{ fill: '#94a3b8', fontSize: 11 }}
                        label={{ value: 'Cumulative Movement', angle: -90, position: 'insideLeft', fill: '#94a3b8', fontSize: 12 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                        wrapperStyle={{ paddingTop: '10px' }}
                        iconType="line"
                        formatter={(value) => <span className="text-slate-300 text-xs">{value}</span>}
                    />
                    <Line 
                        type="monotone" 
                        dataKey="value" 
                        stroke="#3b82f6" 
                        strokeWidth={2}
                        dot={<CustomDot />}
                        activeDot={{ r: 6 }}
                        name="Direction Trend"
                    />
                </LineChart>
            </ResponsiveContainer>
                </div>
            </div>
            
            <div className="mt-3 pt-3 border-t border-slate-800 space-y-2">
                <div className="flex items-center justify-center gap-6 text-xs">
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-green-500 rounded"></div>
                        <span className="text-slate-400">UP = +1</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-red-500 rounded"></div>
                        <span className="text-slate-400">DOWN = -1</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-blue-500 rounded"></div>
                        <span className="text-slate-400">Cumulative Trend</span>
                    </div>
                </div>
                <div className="flex items-center justify-center gap-4 text-xs">
                    <div className="flex items-center gap-1 text-slate-500">
                        <span>🖱️</span>
                        <span>Scroll to navigate • Auto-updates every 30s</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="text-slate-500">Live</span>
                    </div>
                </div>
            </div>
        </div>
    );
};
